/**
 * Route Error Boundary Component
 *
 * Catches and handles errors during lazy route loading (ChunkLoadError, network failures).
 * Provides retry mechanism and user-friendly error messages.
 *
 * Features:
 * - Catches ChunkLoadError (network failure during chunk fetch)
 * - Retry mechanism (up to 3 attempts)
 * - User-friendly error UI
 * - Error logging to console (and optionally to error tracking service)
 */

import React from 'react'

interface RouteErrorBoundaryProps {
  children: React.ReactNode
}

interface RouteErrorBoundaryState {
  hasError: boolean
  error: Error | null
  retryCount: number
}

export class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    // Update state to display fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    console.error('Route loading error:', error, errorInfo)

    // Send to error tracking service in production
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      ;(window as any).Sentry.captureException(error, {
        extra: errorInfo,
        tags: { errorType: 'route-loading' },
      })
    }

    // Log to analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      ;(window as any).gtag('event', 'exception', {
        description: `Route loading error: ${error.message}`,
        fatal: false,
      })
    }
  }

  handleRetry = () => {
    const { retryCount } = this.state

    // Limit retries to 3
    if (retryCount < 3) {
      this.setState({
        hasError: false,
        error: null,
        retryCount: retryCount + 1,
      })

      // Reload the page to refetch chunks
      window.location.reload()
    }
  }

  render() {
    const { hasError, error, retryCount } = this.state

    if (hasError) {
      const isChunkLoadError =
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('Failed to fetch') ||
        error?.name === 'ChunkLoadError'

      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            {/* Error Icon */}
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full">
              <svg
                className="w-6 h-6 text-red-600 dark:text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Error Title */}
            <h2 className="text-xl font-semibold text-red-600 dark:text-red-500 mb-4 text-center">
              {isChunkLoadError ? 'Failed to Load Page' : 'Something Went Wrong'}
            </h2>

            {/* Error Description */}
            <p className="text-gray-600 dark:text-gray-400 mb-4 text-center">
              {isChunkLoadError
                ? 'We encountered an error while loading this page. This usually happens due to network issues or outdated cached files.'
                : 'An unexpected error occurred. Please try refreshing the page.'}
            </p>

            {/* Error Details (in development) */}
            {import.meta.env.DEV && error && (
              <details className="mb-6">
                <summary className="text-sm text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                  Error details
                </summary>
                <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900 p-3 rounded overflow-x-auto">
                  {error.message}
                  {'\n\n'}
                  {error.stack}
                </pre>
              </details>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              {retryCount < 3 ? (
                <button
                  onClick={this.handleRetry}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                >
                  Retry ({retryCount}/3)
                </button>
              ) : (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-500 text-center">
                    Maximum retries reached. Please check your internet connection.
                  </p>
                </div>
              )}

              <a
                href="/"
                className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md font-medium text-center transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              >
                Go Home
              </a>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
